import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

interface ParsedUser {
  email: string
  firstName: string
  lastName: string
  middleName?: string
  mobileNumber?: string
  workPhone?: string
  rowNumber: number
  errors: string[]
}

interface ImportPreviewProps {
  data: ParsedUser[]
  fileName: string
  onImport: () => void
  onCancel: () => void
}

export function ImportPreview({ data, fileName, onImport, onCancel }: ImportPreviewProps) {
  const validUsers = data.filter(u => u.errors.length === 0)
  const invalidUsers = data.filter(u => u.errors.length > 0)

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Import Preview: {fileName}</CardTitle>
          <CardDescription>
            Review the data before importing. Fix any errors in your CSV and re-upload if needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center">{data.length}</p>
                <p className="text-sm text-muted-foreground text-center">Total Rows</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center text-primary">{validUsers.length}</p>
                <p className="text-sm text-muted-foreground text-center">Valid Users</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-3xl font-bold text-center text-destructive">{invalidUsers.length}</p>
                <p className="text-sm text-muted-foreground text-center">Errors</p>
              </CardContent>
            </Card>
          </div>

          {invalidUsers.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {invalidUsers.length} rows have errors and will be skipped. Fix errors in your CSV and re-upload to include them.
              </AlertDescription>
            </Alert>
          )}

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Row</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone Numbers</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((user) => (
                  <TableRow key={user.rowNumber}>
                    <TableCell>{user.rowNumber}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.firstName} {user.middleName && `${user.middleName} `}{user.lastName}
                    </TableCell>
                    <TableCell>
                      {user.mobileNumber && <p className="text-sm">M: {user.mobileNumber}</p>}
                      {user.workPhone && <p className="text-sm">W: {user.workPhone}</p>}
                    </TableCell>
                    <TableCell>
                      {user.errors.length === 0 ? (
                        <Badge variant="outline" className="text-primary border-primary">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Valid
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="mr-1 h-3 w-3" />
                          {user.errors.join(', ')}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onImport} disabled={validUsers.length === 0}>
              Import {validUsers.length} User{validUsers.length !== 1 ? 's' : ''}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
